/**
 * Everything `/` does that is not JSX: the start gate, the intro player, the
 * Main Menu's state, and which of the three currently owns the canvas.
 *
 * It lives outside the component because all of it is imperative and none of
 * it is reactive — one mutable record plus plain functions over it, in the
 * same shape `shell-audio.ts` uses. The component keeps a single `ShellView`
 * signal for the bit of DOM chrome that sits outside the frame.
 *
 * The ordering the whole lane exists to guarantee reads out of `dispatch`:
 * a gesture arms, `arm` awaits `audio.unlock()` (which awaits `resume()`)
 * before signalling `audioReady`, and only a state that has both readiness
 * signals *and* the gesture becomes `playing` — which is the only thing that
 * ever starts the clock.
 */

import { markBootHealthy } from "../offline/boot-health.ts";
import { isUnstaleShortcut, unstale, UNSTALE_MESSAGE } from "../offline/unstale.ts";
import { createShellAudio, type ShellAudio } from "./audio/shell-audio.ts";
import { isFramed } from "./frame-detect.ts";
import { GATE_FONT, gateFrameAt, gateScreenOps } from "./intro/gate-screen.ts";
import { createIntroClock } from "./intro/intro-clock.ts";
import { createIntroPlayer, type IntroPlayer } from "./intro/intro-player.ts";
import type { IntroTick } from "./intro/intro-ticks.ts";
import {
  advanceGate,
  createGateState,
  type GateEvent,
  type GatePhase,
  type GateState,
  gateVisible,
} from "./intro/start-gate.ts";
import {
  activateSelection,
  activeScreen,
  applyAction,
  cancelScreen,
  initialShellState,
  type MenuTree,
  moveSelection,
  pushScreen,
  setSelection,
  type ShellState,
  type ShellTransition,
} from "./model/menu-model.ts";
import { buildShellTree } from "./model/red-baron-shell.ts";
import { createLogicalSurface } from "./render/canvas-painter.ts";
import { noticeScreenOps } from "./render/notice-screen.ts";
import { buildScreenView } from "./render/screen-view.ts";
import type { ShellAssets } from "./render/shell-assets.ts";
import { createShellSurface, type ShellSurface } from "./render/shell-surface.ts";
import { bootShell } from "./shell-boot.ts";

const INTRO_SEEN_KEY = "red-baron/intro-seen";

/** Private-mode storage refusal only costs a replayed intro, never correctness. */
function readIntroSeen(): boolean {
  try {
    return window.sessionStorage.getItem(INTRO_SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

function writeIntroSeen(): void {
  try {
    window.sessionStorage.setItem(INTRO_SEEN_KEY, "1");
  } catch {
    return;
  }
}

/**
 * Keys the browser does not count as user activation, so they cannot be the
 * gesture that unlocks audio: pressing one at the gate would open a silent
 * intro. `Escape` is on the list, which is why it does nothing at the gate
 * even though it is the original's own skip key — once the intro is running
 * the page is already unlocked and every key skips, `Escape` included.
 */
const NON_ACTIVATING_KEYS: ReadonlySet<string> = new Set([
  "Alt",
  "CapsLock",
  "Control",
  "Escape",
  "Meta",
  "NumLock",
  "ScrollLock",
  "Shift",
  "Tab",
]);

function isActivationKey(event: KeyboardEvent): boolean {
  return !event.ctrlKey && !event.metaKey && !event.altKey && !NON_ACTIVATING_KEYS.has(event.key);
}

function preventScroll(event: KeyboardEvent): void {
  if (event.key === " " || event.key.startsWith("Arrow")) event.preventDefault();
}

type MenuCommand = "up" | "down" | "activate" | "cancel";

function menuCommandFor(key: string): MenuCommand | undefined {
  if (key === "ArrowUp") return "up";
  if (key === "ArrowDown") return "down";
  if (key === "Enter" || key === " ") return "activate";
  if (key === "Escape") return "cancel";
  return undefined;
}

export type ShellStatus = "loading" | "ready" | "failed";

/** The only thing the component renders around the canvas. */
export interface ShellView {
  readonly phase: GatePhase;
  readonly status: ShellStatus;
  /** The fonts are in, so the gate draws its own prompt and the DOM's loading line can go. */
  readonly prompting: boolean;
}

interface RuntimeState {
  readonly tree: MenuTree;
  readonly surface: ShellSurface;
  readonly audio: ShellAudio;
  readonly navigate: (path: string) => void;
  listener: ((view: ShellView) => void) | undefined;
  menu: ShellState;
  gate: GateState;
  status: ShellStatus;
  prompting: boolean;
  assets: ShellAssets | undefined;
  intro: IntroPlayer | undefined;
  ticks: readonly IntroTick[];
  phaseStartedAt: number;
  gateHandle: number;
  /** Navigating away mid-load must not let the boot's tail start an intro on a canvas nobody is showing. */
  disposed: boolean;
}

const GESTURE: GateEvent = { kind: "gesture" };

function viewOf(state: RuntimeState): ShellView {
  return { phase: state.gate.phase, status: state.status, prompting: state.prompting };
}

function publish(state: RuntimeState): void {
  state.listener?.(viewOf(state));
}

function setStatus(state: RuntimeState, status: ShellStatus): void {
  state.status = status;
  publish(state);
}

function paintMenu(state: RuntimeState): void {
  if (!state.assets || state.gate.phase !== "menu") return;
  state.surface.paint(state.assets, buildScreenView(state.tree, state.menu, state.assets.fonts).ops);
}

function paintGate(state: RuntimeState): void {
  const frame = gateFrameAt(state.gate, performance.now() - state.phaseStartedAt);
  if (!frame) return;
  const font = state.assets?.fonts.get(GATE_FONT);
  if (!state.assets || !font) {
    state.surface.clear();
    return;
  }
  state.surface.paint(state.assets, gateScreenOps(frame, font));
}

/** The gate's own frame loop; it owns the blink and stops the moment the intro takes the canvas over. */
function gateLoop(state: RuntimeState): void {
  if (!gateVisible(state.gate) || state.status === "failed") return;
  paintGate(state);
  state.gateHandle = requestAnimationFrame(() => gateLoop(state));
}

function repaint(state: RuntimeState): void {
  if (gateVisible(state.gate)) paintGate(state);
  else if (state.intro?.running()) state.surface.blit();
  else paintMenu(state);
}

/**
 * Creating and resuming the context happens inside the gesture's own call
 * stack; only when it resolves does `audioReady` let the machine start the
 * intro, so no tick can precede the unlock.
 */
async function arm(state: RuntimeState): Promise<void> {
  await state.audio.unlock();
  dispatch(state, { kind: "audioReady" });
}

/** The revisit path: no intro, no gate, and the title theme waits for whatever the first input turns out to be. */
async function startMenuMusic(state: RuntimeState): Promise<void> {
  await state.audio.unlock();
  state.audio.playMusic("TITLE", true);
}

function enterMenu(state: RuntimeState): void {
  // Idempotent: a skip needs the player torn down here, a natural finish has already done it.
  state.intro?.skip();
  writeIntroSeen();
  state.audio.playMusic("TITLE", true);
  paintMenu(state);
}

function dispatch(state: RuntimeState, event: GateEvent): void {
  if (state.disposed) return;
  const before = state.gate;
  const after = advanceGate(before, event);
  if (after === before) return;
  state.gate = after;
  if (after.phase === before.phase) {
    if (after.gestured && !before.gestured) void startMenuMusic(state);
    return;
  }
  state.phaseStartedAt = performance.now();
  publish(state);
  if (after.phase === "arming") void arm(state);
  else if (after.phase === "playing") state.intro?.start(state.ticks);
  else if (after.phase === "menu") enterMenu(state);
}

/** Where QUIT sends the tab when this page is not someone else's frame (bug report item 4). */
const QUIT_URL = "https://this.os.isfine.org/";

/**
 * QUIT's own effect (`menu-model.ts`'s `{ kind: "quit" }`): outside a frame
 * there is somewhere real to send the tab, so it goes there directly rather
 * than opening `quit-dialog`'s "there is no DOS to return to" text. Inside a
 * frame, `isFramed()` refuses the top-level navigate and this falls back to
 * exactly what the button always did — pushing `quit-dialog` onto the stack,
 * the same transition `{ kind: "open", screen: "quit-dialog" }` used to
 * produce.
 */
function handleQuit(state: RuntimeState): void {
  if (isFramed()) {
    state.menu = pushScreen(state.tree, state.menu, "quit-dialog");
    paintMenu(state);
    return;
  }
  window.location.href = QUIT_URL;
}

function commit(state: RuntimeState, next: ShellTransition): void {
  state.menu = next.state;
  paintMenu(state);
  if (next.effect?.kind === "navigate") state.navigate(next.effect.path);
  else if (next.effect?.kind === "replayIntro") dispatch(state, { kind: "replayIntro" });
  else if (next.effect?.kind === "quit") handleQuit(state);
}

function menuTransition(state: RuntimeState, command: MenuCommand): ShellTransition {
  if (command === "activate") return activateSelection(state.tree, state.menu);
  if (command === "cancel") return cancelScreen(state.tree, state.menu);
  return { state: moveSelection(state.tree, state.menu, command === "down" ? 1 : -1), effect: undefined };
}

/**
 * The offline escape hatch (`offline/unstale.ts`), answered from anywhere in
 * the shell rather than only from the Main Menu: the whole reason it exists is
 * a worker stale enough to break the boot, and a boot that never finishes
 * never reaches the menu. `/` is the only route that binds it — nothing in
 * flight can trip it, because nothing in flight listens for it.
 *
 * The notice is painted before the clearing starts. If the fonts have not
 * loaded yet there is nothing to paint it with, and the routine still runs:
 * a silent recovery beats no recovery.
 */
function unstaleRequested(state: RuntimeState): void {
  // Both of the shell's animation sources are stopped first, or the gate's own
  // blink loop or a running intro paints straight back over the notice.
  cancelAnimationFrame(state.gateHandle);
  state.intro?.stop();
  const font = state.assets?.fonts.get(GATE_FONT);
  if (state.assets && font) state.surface.paint(state.assets, noticeScreenOps(UNSTALE_MESSAGE, font));
  void unstale();
}

function keyDown(state: RuntimeState, event: KeyboardEvent): void {
  if (isUnstaleShortcut(event)) {
    event.preventDefault();
    unstaleRequested(state);
    return;
  }
  if (state.gate.phase === "playing") {
    preventScroll(event);
    dispatch(state, GESTURE);
    return;
  }
  if (gateVisible(state.gate)) {
    preventScroll(event);
    if (isActivationKey(event)) dispatch(state, GESTURE);
    return;
  }
  if (isActivationKey(event)) dispatch(state, GESTURE);
  const command = state.status === "ready" ? menuCommandFor(event.key) : undefined;
  if (!command) return;
  event.preventDefault();
  commit(state, menuTransition(state, command));
}

function pointerIndex(state: RuntimeState, event: PointerEvent): number | undefined {
  const point = state.surface.logicalPointAt(event.clientX, event.clientY);
  if (!point || !state.assets) return undefined;
  return buildScreenView(state.tree, state.menu, state.assets.fonts).hitTest(point.x, point.y);
}

function pointerDown(state: RuntimeState, event: PointerEvent): void {
  // Read the phase first: the click that skips the intro must not also press whatever menu button it lands over.
  const phase = state.gate.phase;
  dispatch(state, GESTURE);
  if (phase !== "menu" || state.status !== "ready") return;
  const index = pointerIndex(state, event);
  if (index === undefined) return;
  const next = setSelection(state.tree, state.menu, index);
  commit(state, applyAction(state.tree, next, activeScreen(state.tree, next)?.items[index]?.action));
}

function pointerMove(state: RuntimeState, event: PointerEvent): void {
  if (state.status !== "ready" || state.gate.phase !== "menu") return;
  const index = pointerIndex(state, event);
  if (index !== undefined) commit(state, { state: setSelection(state.tree, state.menu, index), effect: undefined });
}

async function boot(state: RuntimeState): Promise<void> {
  const booted = await bootShell(state.audio, {
    onFonts: (assets) => {
      state.assets = assets;
      state.prompting = true;
      publish(state);
      paintGate(state);
    },
    onArtwork: (assets) => {
      state.assets = assets;
      // Revisit path: the Main Menu is on screen the moment its artwork is, with the songs still loading behind it.
      paintMenu(state);
    },
  });
  state.ticks = booted.ticks;
  state.intro = createIntroPlayer({
    scene: createLogicalSurface(),
    target: state.surface.logical,
    assets: booted.assets,
    audio: state.audio,
    clock: createIntroClock(),
    onFrame: () => state.surface.blit(),
    onFinished: () => dispatch(state, { kind: "introFinished" }),
  });
  setStatus(state, "ready");
  // The shell is up and takes keys: this boot got where it was going, so the
  // offline heartbeat's failure counter goes back to zero (`boot-health.ts`).
  markBootHealthy();
  dispatch(state, { kind: "assetsReady" });
}

export interface ShellRuntime {
  view(): ShellView;
  subscribe(listener: (view: ShellView) => void): void;
  mount(canvas: HTMLCanvasElement | undefined): void;
  keyDown(event: KeyboardEvent): void;
  pointerDown(event: PointerEvent): void;
  pointerMove(event: PointerEvent): void;
  touchStart(): void;
  visibilityChanged(hidden: boolean): void;
  repaint(): void;
  dispose(): void;
}

export function createShellRuntime(navigate: (path: string) => void): ShellRuntime {
  const tree = buildShellTree();
  const state: RuntimeState = {
    tree,
    surface: createShellSurface(),
    audio: createShellAudio(),
    navigate,
    listener: undefined,
    menu: initialShellState(tree),
    gate: createGateState(!readIntroSeen()),
    status: "loading",
    prompting: false,
    assets: undefined,
    intro: undefined,
    ticks: [],
    phaseStartedAt: 0,
    gateHandle: 0,
    disposed: false,
  };

  return {
    view: () => viewOf(state),
    subscribe(listener) {
      state.listener = listener;
    },
    mount(canvas) {
      state.surface.attach(canvas);
      state.phaseStartedAt = performance.now();
      if (gateVisible(state.gate)) gateLoop(state);
      void boot(state).catch(() => setStatus(state, "failed"));
    },
    keyDown: (event) => keyDown(state, event),
    pointerDown: (event) => pointerDown(state, event),
    pointerMove: (event) => pointerMove(state, event),
    /** Belt and braces for touch stacks that fire `touchstart` well ahead of `pointerdown`; only the gate needs the earliest possible gesture. */
    touchStart() {
      if (gateVisible(state.gate)) dispatch(state, GESTURE);
    },
    /**
     * A hidden tab stops `requestAnimationFrame` but not wall clock, so an
     * intro left running would come back several ticks ahead of its own music.
     * Both halves freeze instead, and resume together.
     */
    visibilityChanged(hidden) {
      if (state.gate.phase !== "playing") return;
      if (hidden) state.intro?.pause();
      else state.intro?.resume();
    },
    repaint: () => repaint(state),
    dispose() {
      state.disposed = true;
      cancelAnimationFrame(state.gateHandle);
      state.intro?.stop();
      state.audio.dispose();
    },
  };
}
